import { useEffect, useRef, useMemo } from 'react';
import { useRouter } from 'next/router';
import type { NextPage } from 'next';
import { NextUrqlPageContext } from 'next-urql';
import Script from 'next/script';
import { OperationResult, OperationResultSource, UseQueryState } from 'urql';

import { removeLeadingSlash } from 'utils/removeLeadingSlash';
import { checkIfUriGenerationChanged } from 'utils/checkIfUriGenerationChanged';
import {
  fetchBreadcrumbRedirectPromises,
  createProductBreadcrumbLinks,
} from 'utils/breadcrumbRedirects';
import { CATEGORY_BY_ID_QUERY } from 'gql/queries/category';
import { VARIANT_QUERY } from 'gql/queries/product';
import { MainFooterQuery as MainFooterDocument } from 'groq/queries/MainFooterQuery';
import {
  Inventory,
  PdpDocument,
  PixleeConfigDocument,
  PixleeConfigQuery,
  PixleeConfigQueryVariables,
  ProductQueryVariables,
  ProductScoreDocument,
  ProductScoreQuery,
  ProductScoreQueryVariables,
  UserGeneratedContentDocument,
  UserGeneratedContentQuery,
  UserGeneratedContentQueryVariables,
  Variant,
  VariantQuery,
  VariantQueryVariables,
  PdpQuery,
} from '__generated__/graphql';
import { PdpProvider, usePdp } from 'hooks/usePdp';
import { useAuth } from 'hooks/useAuth';
import { useCart } from 'hooks/useCart';
import { useMiniCart } from 'hooks/useMiniCart';
import { usePreferences } from 'hooks/usePreferences';
import { useSiteConfig } from 'hooks/useSiteConfig';
import { useTranslate } from 'hooks/useTranslations';
import { MiniCart } from 'ui/components/MiniCart';
import { Page } from 'ui/components/Page';
import { PageHead } from 'ui/components/PageHead';
import { ProductDetailsPage } from 'ui/pages/ProductDetailsPages';
import NotFoundPage from 'pages/404';
import {
  event,
  getCommonFitAnalyticsData,
  getPDPFitAnalyticsData,
  AnalyticsEvents,
  transformProductToAnalyticsProduct,
} from 'utils/analytics';
import { REFERRER_TYPE, isServer, ELEVATED_PDP_TYPES } from 'utils/constants';
import { Dimensions } from 'types/analyticTypes';
import { useAnalyticsEventAfterPageviewEventHasFired } from 'hooks/useAnalyticsEventAfterPageviewEventHasFired';
import { usePageEventsContext } from 'hooks/usePageEventsContext';
import { buildProductUrl } from 'utils/buildProductUrl';
import { getUrlFromLocale } from 'utils/locale';
import { avoidEmptyProperties } from 'utils/object';
import { getSizesFromSizeGroups } from 'utils/products';
import { useProductHistory } from 'hooks/useProductHistory';
import { useInventory } from 'hooks/useInventory';
import { InjectedScript } from 'ui/elements/InjectedScript';
import { ProductTypeFromPDPQuery } from 'types/product';
import { removeReferrerTypeAndCategory } from 'utils/referrerParametersUtils';
import { CONTENT_QUERY } from 'hooks/useGroqQuery';
import { ProductDetails_PDPTemplateQuery } from 'groq/pages/ProductDetailsPageTemplate';
import { ProductDetails_PDPContentQuery } from 'groq/pages/ProductDetailsPage';
import { GlobalPromoBannerDocument } from 'groq/documents/GlobalPromoBannerDocument';
import { SecondGlobalBannerDocument } from 'groq/documents/SecondGlobalBannerDocument';
import { useModalsOpenStore } from 'store';
import { GWPNewDesign, GWPOldDesign } from 'ui/components/GiftWithPurchase';
import { useFeature } from 'hooks/useFeature';
import { getContentInputQuery } from 'utils/getContentInputQuery';
import { EmplifiEndOfPDPWidget } from 'ui/content/Emplifi/EndOfPDPWidget';
import { EmplifiScript } from 'ui/content/Emplifi/Script';
import { isCrawlerCheck } from 'utils/isCrawlerCheck';
import { useLocation } from 'hooks/useLocation';

const VARIANT_ID_MIN_LENGTH = 12; // variant IDs will be 12+ digits long
/** Checks if the length of the given `id` is gte `VARIANT_ID_MIN_LENGTH`. */
export const isVariantId = (id: string) => id.length >= VARIANT_ID_MIN_LENGTH;
const schemaInStock = 'http://schema.org/InStock';
const schemaOutOfStock = 'http://schema.org/OutOfStock';
const schemaPreOrder = 'https://schema.org/PreOrder';

const getAvailability = (
  inventory: UseQueryState<{ inventory: Inventory }>,
  variation: Variant | undefined | null,
  masterProduct: ProductTypeFromPDPQuery
) => {
  // PreOrder
  if (variation?.displayOutOfStock?.comingsoon) {
    return schemaPreOrder;
  }
  // Size availability
  if (inventory?.data?.inventory) {
    return inventory?.data?.inventory.quantity !== 0
      ? schemaInStock
      : schemaOutOfStock;
  }
  // Color variant availability
  if (variation?.orderable) {
    return schemaInStock;
  }
  // MasterProduct availability
  if (masterProduct?.orderable) {
    return schemaInStock;
  }
  return schemaOutOfStock;
};

const ProductDetails: NextPage = () => {
  const t = useTranslate();
  const router = useRouter();
  const { setPreferences } = usePreferences();
  const {
    countryCode,
    language,
    currency,
    host,
    env,
    localizeUrlPath,
    staticFeatures,
  } = useSiteConfig();
  const { pixleeProductDiscovery } = usePageEventsContext();
  const auth = useAuth();
  const hasSentAnalytics = useRef<string | undefined>();
  const { addToCart, cart } = useCart();
  const { isModalOpen } = useModalsOpenStore();
  const {
    desiredProductId,
    desiredModel,
    desiredSize,
    desiredSwatch,
    hasLoaded,
    masterProduct,
    variation,
    productQuery,
    lazyProductQuery,
    setAddToCartError,
    setAddToCartReturnCode,
    referrerCategoryId,
    content,
    productScore,
    // Content PDP Query Imports - Using the two condensed queries
    pdpContentQuery,

    // Content PDP Template Query Import - Using the single template query
    productDetailsTemplateContent,
  } = usePdp();

  const isGiftWithPurchaseNewDesign = useFeature(
    'GIFT_WITH_PURCHASE_NEW_DESIGN'
  );

  const selectedSwatch = masterProduct?.variations.find(
    variation => variation.colorValue === desiredSwatch
  ) as Variant;

  const sizes = selectedSwatch
    ? getSizesFromSizeGroups(selectedSwatch?.sizeGroups)
    : [];
  const selectedSize = sizes.find(item => item.id === desiredSize);

  const [inventory] = useInventory(selectedSize?.productId);

  let previousPlpPath = '';
  if (typeof window !== 'undefined' && window.localStorage) {
    const storedPath = localStorage.getItem('productClickedPlpUrl');

    if (storedPath) previousPlpPath = storedPath;
    else previousPlpPath = 'non-plp';
  }

  const colors = masterProduct?.colors || [];
  const hasManyColors = colors.length > 1;

  const getVariantSchema = (
    masterProduct: ProductTypeFromPDPQuery,
    currencyCode: string,
    masterDescription?: string | null
  ) => {
    return masterProduct?.variations?.map(
      ({
        masterId,
        name,
        colorValue,
        preview,
        colorName,
        salePrice,
        price,
        orderable,
      }) => {
        const variantPath = buildProductUrl(masterId, name, {
          color: colorValue,
        });

        return masterId || name
          ? {
              '@type': 'Product',
              name: `${name} - ${colorName}`,
              image: preview,
              description: masterDescription,
              color: colorName,
              offers: {
                '@type': 'Offer',
                url: localizeUrlPath(variantPath, true),
                priceCurrency: currencyCode,
                price: salePrice || price,
                itemCondition: 'https://schema.org/NewCondition',
                availability: orderable ? schemaInStock : schemaOutOfStock,
              },
            }
          : undefined;
      }
    );
  };

  useEffect(() => {
    //For the scenario when user hits a new url in the same browser window
    const handleBeforeUnload = () => {
      removeReferrerTypeAndCategory();
    };
    //For the scenario when user switch tabs
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        removeReferrerTypeAndCategory();
      }
    };
    //For the scenario when user goes back or forward using browser arrows
    const handlePopState = () => {
      removeReferrerTypeAndCategory();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('popstate', handlePopState);
    return () => {
      localStorage.removeItem('productClickedPlpUrl');
      removeReferrerTypeAndCategory();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  const { isMiniCartVisible, closeMiniCart } = useMiniCart();

  useAnalyticsEventAfterPageviewEventHasFired(() => {
    const sizes = getSizesFromSizeGroups(variation?.sizeGroups ?? []);
    const itemId =
      sizes?.find(s => s.id === desiredSize)?.productId ??
      variation?.id ??
      masterProduct?.id;

    if (
      productQuery.fetching ||
      !masterProduct ||
      hasSentAnalytics.current === itemId
    )
      return;

    if (productQuery.data && variation && masterProduct) {
      const product = productQuery.data?.product;

      const productDiscovery =
        window.localStorage.getItem(REFERRER_TYPE) ??
        (pixleeProductDiscovery ? 'pixlee' : '');

      if (pixleeProductDiscovery) {
        event(AnalyticsEvents.PROMOTION_CLICK, {
          ecommerce: {
            promoClick: {
              promotions: [
                {
                  id: product?.id,
                  name: 'How To Wear It',
                  creative: 'pixlee',
                  position: '1',
                  cta: 'Buy Now',
                },
              ],
            },
          },
        });
      }

      if (variation) {
        event(AnalyticsEvents.PRODUCT_VIEWED, {
          currencyCode: currency.code,
          ecommerce: {
            detail: {
              actionField: {
                list: router.query.search ? 'internal search' : previousPlpPath,
              },
              products: [
                {
                  ...transformProductToAnalyticsProduct({
                    categoryId: referrerCategoryId,
                    desiredSize,
                    includeExtraAnalyticsDimensions: true,
                    product,
                    variant: variation,
                  }),
                  [Dimensions.d100]: productDiscovery,
                },
              ],
            },
          },
        });
        hasSentAnalytics.current = itemId;
      }

      const isElevated = content?.aboveProductStory !== undefined;
      if (isElevated) {
        event(AnalyticsEvents.ELEVATED_PDP_IMPRESSION);
      }
    }
  }, [desiredProductId, productQuery, variation, masterProduct, desiredSize]);

  // On mount, start session, add product to last viewed product preferences
  useEffect(() => {
    if (desiredSwatch) {
      const currentItem = {
        id: desiredProductId,
        name: desiredModel,
        swatch: desiredSwatch,
        size: desiredSize,
        country: countryCode,
      };

      window.sessionStorage.setItem('in-session', 'true');

      setPreferences(curr => ({
        ...curr,
        'last-viewed-product': currentItem,
      }));
    }

    return () => {
      closeMiniCart();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (desiredSwatch && variation?.name) {
      const currentItem = {
        id: desiredProductId,
        name: variation.name,
        swatch: desiredSwatch,
      };

      setPreferences(curr => ({
        ...curr,
        'last-ga4-item': currentItem,
      }));
    }
  }, [desiredProductId, desiredSwatch, variation?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const { pushItemToProductHistory } = useProductHistory();

  useEffect(() => {
    if (desiredProductId && desiredSwatch)
      pushItemToProductHistory({
        id: desiredProductId,
        color: desiredSwatch,
      });
  }, [desiredProductId, desiredSwatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset cart error when changing size/color.
  useEffect(() => {
    setAddToCartError(undefined);
    setAddToCartReturnCode(undefined);
  }, [desiredSize, desiredSwatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // If swatch or size from URL are invalid, remove from query and update router.
  // If variation isn't set you could end up in an infinite loop
  // make sure variation is always checked here
  if (
    !isServer &&
    (!productQuery.fetching || (productQuery.fetching && productQuery.stale)) &&
    !lazyProductQuery.fetching &&
    variation
  ) {
    const sizes = getSizesFromSizeGroups(variation.sizeGroups) ?? [];

    if (router.query.swatch && router.query.swatch !== variation.colorValue) {
      router.replace({
        query: { ...router.query, swatch: variation.colorValue },
      });
    }

    if (desiredSize) {
      const size = sizes.find(x => x.id === desiredSize);
      // if requested size doesn't exists remove it from the query
      if (!size) {
        const { size: _, ...query } = router.query;
        router.replace({ query });
      }
    } else {
      if (sizes.length === 1) {
        router.replace({ query: { ...router.query, size: sizes[0].id } });
      }
    }
  }
  const { localeUrlsRef, localeUrlsState, setLocaleUrlsState } = useLocation();

  const localeUrls = useMemo(() => {
    if (!masterProduct?.productNameTranslated) return null;

    const urls = Object.entries(masterProduct.productNameTranslated).reduce(
      (acc: Record<string, any>, [lang, name]) => {
        acc[lang] = removeLeadingSlash(
          buildProductUrl(masterProduct.id, String(name || ''))
        );
        return acc;
      },
      {}
    );

    return { path: router.asPath, ...urls };
  }, [masterProduct?.id, masterProduct?.productNameTranslated, router.asPath]);

  if (localeUrlsRef) {
    localeUrlsRef.current = localeUrls;
  }

  useEffect(() => {
    if (!localeUrlsState?.path || localeUrlsState?.path !== router.asPath) {
      setLocaleUrlsState(localeUrls);
    }
  }, [setLocaleUrlsState, localeUrls, router.asPath, localeUrlsState?.path]);

  useEffect(() => {
    if (masterProduct && variation) {
      const fitAnalytics = {
        ...getCommonFitAnalyticsData(
          language,
          countryCode,
          currency.code,
          auth.customerId,
          window.fitAnalytics?.sessionId || ''
        ),
        ...getPDPFitAnalyticsData(
          masterProduct,
          variation,
          variation.colorValue
        ),
        operations: {
          addToCart: async (
            productId: string,
            desiredSize: string,
            quantity = 1
          ) => {
            const variation = masterProduct.variations.find(
              product => product.styleNumber === productId
            ) as Variant;
            if (variation) {
              const sizes = getSizesFromSizeGroups(variation.sizeGroups);
              const findCorrectSize = sizes.find(
                size => size.label === desiredSize
              );
              const currentUrl = router.asPath;
              await addToCart(
                {
                  swatch: variation!.colorValue,
                  size: findCorrectSize?.id,
                  quantity,
                  productId: findCorrectSize?.productId || '',
                  productCustomization: undefined,
                  masterId: masterProduct.id,
                },
                {},
                true,
                currentUrl
              );
            }
          },
        },
      };
      window.fitAnalytics = fitAnalytics;
    }
  }, [
    masterProduct,
    variation,
    language,
    countryCode,
    currency.code,
    auth.customerId,
    desiredSwatch,
    desiredSize,
    addToCart,
    router.asPath,
  ]);

  const shouldShowNotFoundPage = hasLoaded && !(masterProduct && variation);
  if (shouldShowNotFoundPage) {
    return <NotFoundPage message={t('productCouldNotBeFound')} />;
  }

  // only enable fitanalytics in CSR when configured on non local envs
  const shouldEnableFitAnalytics =
    !isServer &&
    location.hostname.indexOf('localhost') === -1 &&
    env.fitAnalyticsPdpUrl;

  const combinedProductStories = [
    ...(content?.aboveProductStory || []),
    ...(content?.belowProductStory || []),
  ];

  const hreflangs: Record<string, string> = {
    ...Object.fromEntries(
      Object.entries(
        (masterProduct?.productNameTranslated as Record<string, string>) || {}
      )
        .map(([key, value]) => [
          key,
          String(buildProductUrl(masterProduct?.id || '', value || '')),
        ])
        .filter(([_, value]) => value !== undefined)
    ),
    [language]: buildProductUrl(
      masterProduct?.id || '',
      masterProduct?.name || ''
    ),
  };
  const isElevatedPdp = combinedProductStories.some(item => {
    if (item?.component && '_type' in item.component) {
      const componentType = item?.component?._type;
      return componentType && ELEVATED_PDP_TYPES.includes(componentType);
    }
  });
  const productId = masterProduct?.id || variation?.masterId || '';
  const productName =
    productQuery?.data?.product?.name ||
    variation?.name ||
    masterProduct?.name ||
    '';

  const shouldDisplayReviews =
    staticFeatures.isPNARegion &&
    variation?.id &&
    !masterProduct?.disableReviews;

  return (
    <Page
      query={[
        {
          ...productQuery,
          fetching:
            (productQuery.fetching && !productQuery.data) ||
            (productQuery.fetching &&
              desiredProductId !== productQuery.data?.product?.id),
        },
        // Content PDP Queries Data - Using the single condensed query
        { ...pdpContentQuery },

        // Content PDP Template Query Data - Using the single template query
        { ...productDetailsTemplateContent },
      ]}
      emarsysData={{
        skipTracking: false,
      }}
      bloomreachData={{
        prod_id: `${masterProduct?.id}_${variation?.colorValue}`,
        prod_name: masterProduct?.name,
        sku: variation?.ean || undefined,
      }}
      pageViewPageType={isElevatedPdp ? 'elevated pdp' : 'pdp'}
    >
      {shouldEnableFitAnalytics && (
        <Script src={env.fitAnalyticsPdpUrl} strategy="afterInteractive" />
      )}
      {staticFeatures.isPNARegion && variation?.id && (
        <EmplifiScript
          sku={variation?.id || ''}
          pageId="pdp-page"
          eventData={{
            item_id_ep: variation?.id || '',
            item_name_ep: masterProduct?.name || '',
            review_count: masterProduct?.amountOfReviews,
            avg_rating: masterProduct?.averageRating,
          }}
        />
      )}
      <PageHead
        title={t('pageTitleReduced', {
          title:
            masterProduct?.name ||
            decodeURIComponent(desiredModel).replace(/-/g, ' '),
        })}
        openGraphType="product"
        description={masterProduct?.description ?? ''}
        canonical={buildProductUrl(productId, productName)}
        hreflangs={masterProduct?.productNameTranslated ? hreflangs : undefined}
        openGraphImageUrl={variation?.preview}
        twitterImageUrl={variation?.preview}
      />
      <InjectedScript
        type="application/ld+json"
        script={JSON.stringify(
          avoidEmptyProperties({
            '@context': 'http://schema.org',
            '@type': 'Product',
            name: masterProduct?.name,
            sku: variation?.ean,
            color: variation?.colorName,
            description: masterProduct?.description,
            model: masterProduct?.id,
            brand: {
              '@type': 'Brand',
              name: 'PUMA',
            },
            logo: `https://${host}/assets/logo-puma-black.png`,
            image: variation?.images.map(x => x.href),
            offers: {
              '@type': 'Offer',
              price: variation?.salePrice || variation?.price,
              priceCurrency: currency.code,
              availability: getAvailability(
                inventory,
                variation,
                masterProduct
              ),
              availabilityStarts:
                variation?.displayOutOfStock?.validTo || undefined,
              priceValidUntil: variation?.validUntil,
              priceSpecification:
                variation?.salePrice !== variation?.price
                  ? {
                      '@type': 'UnitPriceSpecification',
                      priceType: 'https://schema.org/StrikethroughPrice',
                      price: variation?.price,
                      priceCurrency: currency.code,
                    }
                  : undefined,
              url: !isServer ? window.location.href : router.asPath,
              seller: {
                '@type': 'Corporation',
                name: 'PUMA',
              },
            },
            aggregateRating:
              productScore && Number(productScore.amount) > 0
                ? {
                    '@type': 'AggregateRating',
                    ratingValue: productScore.rating,
                    reviewCount: productScore.amount,
                    bestRating: 5,
                    worstRating: 1,
                  }
                : undefined,
            review: (productScore?.reviews?.nodes || []).map(review => ({
              '@type': 'Review',
              author: {
                '@type': 'Person',
                name: review.nickname || 'anonymous',
              },
              datePublished: review.date,
              name: review.title,
              reviewBody: review.text,
              reviewRating: {
                '@type': 'Rating',
                ratingValue: review.rating,
                bestRating: 5,
                worstRating: 1,
              },
            })),
          })
        )}
      />
      {hasManyColors && (
        <InjectedScript
          type="application/ld+json"
          script={JSON.stringify(
            avoidEmptyProperties({
              '@context': 'http://schema.org',
              '@type': 'ProductGroup',
              name: masterProduct?.name,
              sku: variation?.ean,
              url: `https://${host}${router.asPath.split('?')[0]}`,
              description: masterProduct?.description,
              brand: {
                '@type': 'Brand',
                name: 'PUMA',
              },
              productGroupID: masterProduct?.id,
              variesBy: 'https://schema.org/color',
              hasVariant: getVariantSchema(
                masterProduct,
                currency.code,
                masterProduct?.description
              ),
            })
          )}
        />
      )}
      {variation && masterProduct && <ProductDetailsPage />}
      {isMiniCartVisible && <MiniCart key={masterProduct?.id} />}
      {isModalOpen('GWPModal') ? (
        isGiftWithPurchaseNewDesign ? (
          <GWPNewDesign.Modal cart={cart} />
        ) : (
          <GWPOldDesign.Modal cart={cart} />
        )
      ) : null}
      {shouldDisplayReviews && (
        <EmplifiEndOfPDPWidget sku={variation?.id || ''} />
      )}
    </Page>
  );
};

const Pdp = props => {
  return (
    <PdpProvider>
      <ProductDetails {...props} />
    </PdpProvider>
  );
};

if (isServer) {
  Pdp.getInitialProps = async (context: NextUrqlPageContext) => {
    const id = context.query.id as string;

    const req = context.req!;
    const { host } = req.headers;

    const namespace = process.env.NAMESPACE;

    const isCrawler = isCrawlerCheck({ host, namespace, req });

    const queriesParellelizationList: Array<
      OperationResultSource<OperationResult>
    > = [];

    if (isVariantId(id)) {
      const { data } = await context.urqlClient
        .query<VariantQuery, VariantQueryVariables>(VARIANT_QUERY, { id })
        .toPromise();

      if (!data?.variant) {
        if (context.res) context.res.statusCode = 404;
        return {}; // Fall through to allow client-side 404 page to render
      }

      const { masterId, name, colorValue, size } = data?.variant;

      const queryString = context.asPath?.split('?')[1];
      const additionalParams = queryString?.split('&').reduce((acc, entry) => {
        const [key, value] = entry.split('=');
        acc[key] = value;
        return acc;
      }, {});

      const variantPath = buildProductUrl(masterId, name, {
        color: colorValue,
        size: size.value,
        ...additionalParams,
      });

      const localePrefix = getUrlFromLocale(
        context.req?.headers.locale as string
      );
      const relativePath = `${localePrefix}${variantPath}`;
      const host = context.req?.headers.host;
      const protocol = host?.includes('localhost') ? 'http' : 'https';

      const fullPath = `${protocol}://${host}${relativePath}`;
      const redirectUrl = host ? fullPath : relativePath;

      // eslint-disable-next-line no-console
      console.warn('Redirect with Variant ID', {
        from: id,
        to: variantPath,
      });

      return context.res?.writeHead(301, { Location: redirectUrl }).end();
    }

    const pdpQuery = context.urqlClient.query<PdpQuery, ProductQueryVariables>(
      PdpDocument,
      { id }
    );

    // Content PDP Template Query - Using the single consolidated template query
    const templateContentQuery = context.urqlClient.query(
      CONTENT_QUERY('ProductDetails_PDPTemplate'),
      getContentInputQuery({
        query: ProductDetails_PDPTemplateQuery,
        context,
      })
    );

    if (isCrawler) {
      //Content PDP Queries - Using the two condensed queries
      const pdpContentQuery = context.urqlClient.query(
        CONTENT_QUERY('PDP_ProductDetails'),
        getContentInputQuery({
          query: ProductDetails_PDPContentQuery,
          context,
          params: {
            productId: id,
          },
        })
      );

      queriesParellelizationList.push(pdpContentQuery);
    }

    const globalPromoBannerQuery = context.urqlClient.query(
      CONTENT_QUERY('GlobalPromoBanner'),
      getContentInputQuery({
        query: GlobalPromoBannerDocument,
        context,
      })
    );

    const secondGlobalBannerQuery = context.urqlClient.query(
      CONTENT_QUERY('SecondGlobalBanner'),
      getContentInputQuery({
        query: SecondGlobalBannerDocument,
        context,
      })
    );

    const mainFooterQuery = context.urqlClient.query(
      CONTENT_QUERY('MainFooter'),
      getContentInputQuery({
        query: MainFooterDocument,
        context,
      })
    );

    const pixleeConfigQuery = context.urqlClient.query<
      PixleeConfigQuery,
      PixleeConfigQueryVariables
    >(PixleeConfigDocument, {});

    const productScoreQuery = context.urqlClient.query<
      ProductScoreQuery,
      ProductScoreQueryVariables
    >(ProductScoreDocument, { id, includeReviews: true });

    const userGeneratedContent = context.urqlClient.query<
      UserGeneratedContentQuery,
      UserGeneratedContentQueryVariables
    >(UserGeneratedContentDocument, { productId: id });

    // Parallelize the following queries on SSR
    queriesParellelizationList.push(
      globalPromoBannerQuery,
      secondGlobalBannerQuery,
      mainFooterQuery,
      pixleeConfigQuery,
      productScoreQuery,
      userGeneratedContent,

      templateContentQuery
    );

    const { data } = await pdpQuery.toPromise();

    if (!data?.product) {
      if (context.res) context.res.statusCode = 404;
      return {}; // Fall through to allow client-side 404 page to render
    }

    // Pre-load breadcrumb queries
    if (data.product && data.product.primaryCategoryId) {
      const categoryByIdQuery = context.urqlClient.query<any, { id: string }>(
        CATEGORY_BY_ID_QUERY,
        { id: data.product.primaryCategoryId }
      );

      const categoryResult = await categoryByIdQuery.toPromise();
      const categoryById = categoryResult.data?.categoryById;

      if (categoryById) {
        const breadcrumbLinks = createProductBreadcrumbLinks({
          categoryById,
          productName: data.product.name,
          productLink: `pd/${context.query.model}/${context.query.id}`,
        });

        const redirectPromises = fetchBreadcrumbRedirectPromises(
          context.urqlClient,
          [...breadcrumbLinks]
        );

        queriesParellelizationList.push(...redirectPromises);
      }
    }

    await Promise.all(queriesParellelizationList);

    const name: string = data.product.name || '';
    const currentUrl: string = context.req?.url || '';
    const { check, oldUri, newUri } = checkIfUriGenerationChanged(name);
    const baseUrl: string = `${
      context.req?.headers['x-forwarded-proto'] || 'http'
    }://${context.req?.headers.host}`;
    const fullUrl = new URL(currentUrl, baseUrl);

    if (
      check &&
      context.asPath?.includes(oldUri) &&
      !newUri?.includes(oldUri)
    ) {
      const params: Record<string, string | boolean> = Object.fromEntries(
        new URLSearchParams(fullUrl.search)
      );
      const newProductUrl = buildProductUrl(data.product.id, name, params);
      const localePrefix = getUrlFromLocale(
        context.req?.headers?.locale as string
      );
      const relativePath = `${localePrefix}${newProductUrl}`;
      const host = context.req?.headers.host;
      const protocol = host?.includes('localhost') ? 'http' : 'https';
      const fullPath = `${protocol}://${host}${relativePath}`;
      const redirectUrl = host ? fullPath : relativePath;

      context.res?.writeHead(301, { Location: redirectUrl }).end();
    }

    // return an object that a non-empty to prevent "returned an empty object" error in logs
    return { emptyProps: true };
  };
}

export default Pdp;
